import { buttonStyle, OFF_SECONDARY_CLASS } from 'components/buttons/buttonStyle';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import { ChevronDownIcon, GlobeIcon, UsersIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from 'components/ui/popover';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { NEW_FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { useId, useMemo, useState } from 'react';
import { type Control, type UseFormSetValue, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSetRequiredFieldAudienceMutation } from 'store/api/back-office-settings';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetJobRolesWithFiltersQuery } from 'store/api/jobRoles';
import { useGetSystemRolesListQuery } from 'store/api/systemRoles';
import type { RequiredFieldAudience, RequiredFieldElement, RequiredFieldScope } from 'types/back-office-settings';
import type { EmployeeListItemMinDto } from 'types/employees';
import type { JobRolesListElement } from 'types/jobRoles';
import type { SystemRole } from 'types/systemRoles';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { CLEAR_STICKY_CLASS } from './requiredFieldLayout';
import { useSettingSave } from './useSettingSave';
import { AUDIENCE_TRIGGER_CLASS, ChoiceText, choiceClass } from './WhoHasItPopover';

const COPY = 'back-office-settings.required-fields';

/** The pickers hold what they select; a stored pick carries only its id and name. */
interface AudienceForm {
    scope: RequiredFieldScope;
    jobRoles: JobRolesListElement[];
    systemRoles: SystemRole[];
    employees: EmployeeListItemMinDto[];
}

const storedAudience = (audience: RequiredFieldAudience): AudienceForm => ({
    scope: audience.scope,
    jobRoles: audience.jobRoles as JobRolesListElement[],
    systemRoles: audience.systemRoles as SystemRole[],
    employees: audience.employees.map(({ id, fullName }) => ({ id, name: fullName }) as EmployeeListItemMinDto),
});

/** A cell's chip is a step smaller than a row's, so six fit across the Client form's grid. */
const TRIGGER_SIZE = {
    cell: `h-6 gap-1 ps-2 pe-1.5 text-xs [&_svg]:size-3 ${CLEAR_STICKY_CLASS}`,
    row: 'h-7 gap-1.5 ps-2.5 pe-2 text-[13px] [&_svg]:size-3.5',
};

interface Props {
    entry: RequiredFieldElement;
    /** The field and, on the Client form, the contact kind: "Personal ID code · Person, Client". */
    context: string;
    size: keyof typeof TRIGGER_SIZE;
    readOnly: boolean;
}

/**
 * The "Mandatory for" chip of a mandatory required field and the popover it opens: the whole firm, or the colleagues
 * who hold a picked job role or system role, or are picked by name. Cancel drops the choice; Save writes the
 * audience in one request.
 */
export function MandatoryForPopover({ entry, context, size, readOnly }: Props) {
    const intl = useIntl();
    const hintId = useId();
    const [open, setOpen] = useState(false);
    const [setAudience, { isLoading: isSaving }] = useSetRequiredFieldAudienceMutation();
    const save = useSettingSave();
    const { control, setValue, reset, getValues } = useForm<AudienceForm>({
        defaultValues: storedAudience(entry.audience),
    });
    const scope = useWatch({ control, name: 'scope' });
    const [jobRoles, systemRoles, employees] = useWatch({
        control,
        name: ['jobRoles', 'systemRoles', 'employees'],
    });
    const picked = jobRoles.length + systemRoles.length + employees.length > 0;

    const { audience } = entry;
    const isFirm = audience.scope === 'TENANT';
    const count = audience.jobRoles.length + audience.systemRoles.length + audience.employees.length;
    const shown = isFirm ? t('back-office-settings.whole-firm') : t(`${COPY}.chip-selected`, { count: `${count}` });

    const onOpenChange = (next: boolean) => {
        if (next) reset(storedAudience(audience));
        setOpen(next);
    };

    const onSave = async () => {
        const chosen = getValues();
        const saved = await save(() =>
            setAudience({
                form: entry.form,
                field: entry.field,
                kind: entry.kind,
                scope: chosen.scope,
                jobRoleIds: chosen.jobRoles.map(({ id }) => id),
                systemRoleIds: chosen.systemRoles.map(({ id }) => id),
                employeeIds: chosen.employees.map(({ id }) => id),
            }).unwrap()
        );
        if (saved) setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger
                disabled={readOnly}
                aria-label={intl.formatMessage({ id: `${COPY}.chip-name` }, { shown, context })}
                className={cn(
                    AUDIENCE_TRIGGER_CLASS,
                    'hit-area relative cursor-pointer',
                    TRIGGER_SIZE[size],
                    NEW_FOCUS_RING
                )}
            >
                {isFirm ? <GlobeIcon /> : <UsersIcon />}
                {shown}
                <ChevronDownIcon />
            </PopoverTrigger>
            <PopoverContent
                align="end"
                sideOffset={8}
                positionerClassName="z-(--z-dropdown)"
                className="new:ring-border-menu ring-divider max-h-[min(var(--available-height),640px)] w-[440px] max-w-[calc(100vw-2rem)] gap-3 rounded-[10px] text-[13px]"
            >
                <div className="-mx-1 flex min-h-0 flex-col gap-3 overflow-y-auto overscroll-contain px-1">
                    <div className="flex flex-col gap-0.5">
                        <PopoverTitle className="text-fg text-sm font-semibold">
                            <FormattedMessage id={`${COPY}.mandatory-for`} />
                        </PopoverTitle>
                        <p className="text-grey-600 new:text-muted-foreground m-0 text-xs">{context}</p>
                    </div>
                    <RadioGroup
                        value={scope}
                        onValueChange={(value) => setValue('scope', value as RequiredFieldScope)}
                        aria-label={t(`${COPY}.mandatory-for`)}
                        className="gap-3"
                    >
                        <label className={cn(choiceClass(scope === 'TENANT'), 'cursor-pointer items-start')}>
                            <RadioGroupItem variant="primary" value="TENANT" className="mt-px" />
                            <ChoiceText
                                titleId="back-office-settings.whole-firm"
                                descriptionId={`${COPY}.whole-firm-description`}
                            />
                        </label>
                        <div className={cn(choiceClass(scope === 'SELECTED'), 'flex-col gap-3')}>
                            <label className="flex cursor-pointer items-start gap-2.5">
                                <RadioGroupItem variant="primary" value="SELECTED" className="mt-px" />
                                <ChoiceText
                                    titleId={`${COPY}.selected-only`}
                                    descriptionId={`${COPY}.selected-only-description`}
                                />
                            </label>
                            {scope === 'SELECTED' ? (
                                <div
                                    role="group"
                                    aria-label={t(`${COPY}.selected-only`)}
                                    aria-describedby={hintId}
                                    className="flex flex-col gap-2.5"
                                >
                                    <AudiencePickers control={control} setValue={setValue} />
                                    <p
                                        id={hintId}
                                        className={cn(
                                            'm-0 text-xs',
                                            picked ? 'text-grey-600' : 'text-fg-error new:text-danger-ink'
                                        )}
                                    >
                                        <FormattedMessage
                                            id={picked ? `${COPY}.others-optional` : `${COPY}.pick-one`}
                                        />
                                    </p>
                                </div>
                            ) : null}
                        </div>
                    </RadioGroup>
                    <p className="text-grey-600 m-0 text-xs leading-[17px]">
                        <FormattedMessage id={`${COPY}.no-colleague-note`} />
                    </p>
                </div>
                <div className="flex shrink-0 justify-end gap-2">
                    <Button {...buttonStyle({ variant: 'outlined' }, 'h-8 px-3')} onClick={() => setOpen(false)}>
                        <FormattedMessage id="cancel" />
                    </Button>
                    <Button
                        {...buttonStyle(
                            { variant: 'contained', color: 'secondary' },
                            cn('h-8 px-3.5', OFF_SECONDARY_CLASS)
                        )}
                        disabled={isSaving || (scope === 'SELECTED' && !picked)}
                        focusableWhenDisabled
                        onClick={() => void onSave()}
                    >
                        <FormattedMessage id="save" />
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

/** Mounted only while Selected only is picked, so the system roles are read when the pickers first show. */
function AudiencePickers({
    control,
    setValue,
}: {
    control: Control<AudienceForm>;
    setValue: UseFormSetValue<AudienceForm>;
}) {
    // System roles have no filtered endpoint; the whole list is short, and the picker filters it as you type. No rule
    // can pick a systemic role.
    const { data: allSystemRoles = [] } = useGetSystemRolesListQuery();
    const systemRoles = useMemo(() => allSystemRoles.filter((role) => !role.systemicRole), [allSystemRoles]);
    return (
        <>
            <DynamicMultiSelectInput<AudienceForm, JobRolesListElement>
                control={control}
                setValue={setValue}
                name="jobRoles"
                label={`${COPY}.job-roles`}
                placeholder={`${COPY}.job-roles-placeholder`}
                paginated={{ fetch: useLazyGetJobRolesWithFiltersQuery, searchField: 'name' }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
            <MultiSelectInput<AudienceForm, SystemRole>
                control={control}
                setValue={setValue}
                name="systemRoles"
                label={`${COPY}.system-roles`}
                placeholder={`${COPY}.system-roles-placeholder`}
                options={systemRoles}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
            <DynamicMultiSelectInput<AudienceForm, EmployeeListItemMinDto>
                control={control}
                setValue={setValue}
                name="employees"
                label={`${COPY}.colleagues`}
                placeholder="back-office-settings.add-colleague"
                paginated={{ fetch: useLazyGetEmployeesListWithFiltersQuery, searchField: 'fullName' }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
        </>
    );
}
